import Entity from './entity';
import GroupMember from './group_member';

class Person extends Entity {
    static defaults = {
        uuid: null,
        first_name: null,
        last_name: null,
        email: null,
        phone:null,
        institution_id: null,
        institution: {},
        credentials: [],
        expertises: [],
        profile_photo: null,
        race_id: null,
        coc: null,
        core_member_attestation_completed: false,
        created_at: null,
        updated_at: null,
        deleted_at: null
    }

    static dates = [
        'created_at',
        'updated_at',
        'deleted_at'
    ]

    constructor(attributes = {}) {
        if (attributes instanceof Person) {
            attributes = {
                ...attributes.attributes,
                memberships: [attributes.memberships.map(m => m.attributes)],
            };
        }

        const memberships = attributes.memberships || [];
        delete(attributes.memberships);

        const mailLog = attributes.mailLog || [];
        delete(attributes.mailLog);

        super({...attributes});

        this.memberships = memberships.map(m => new GroupMember(m));
        this.mailLog = mailLog;
    }

    get institutionName () {
        return this.institution ? this.institution.name : null;
    }

    get institutionCity () {
        return this.institution?.city ?? null;
    }

    get institutionCountry () {
        return this.institution?.country ?? null;
    }

    get institutionDisplay () {
        if (!this.institution) {
            return null;
        }

        const location = [this.institutionCity, this.institutionCountry].filter(Boolean).join(', ');
        return location ? `${this.institutionName} — ${location}` : this.institutionName;
    }

    get credentialsString () {
        return this.credentials ? this.credentials.map(c => c.name).join(', ') : null;
    }

    get membershipsWithPendingCois () {
        return this.membershipsWithCoiRequirement.filter(m => m.coi_needed);
    }

    get membershipsWithCompletedCois () {
        return this.membershipsWithCoiRequirement.filter(m => !m.coi_needed);
    }

    get membershipsWithOutdatedCois () {
        return this.membershipsWithCoiRequirement.filter(m => m.coi_needed && m.latest_coi_id !== null);
    }

    get hasPendingCois () {
        return this.membershipsWithPendingCois.length  > 0;
    }

    get hasCompletedCois () {
        return this.membershipsWithCompletedCois.length > 0;
    }

    get hasOutdatedCois () {
        return this.membershipsWithOutdatedCois.length > 0;
    }

    get expertPanelMemberships () {
        return this.memberships.filter(m => [3,4].includes(m.group.group_type_id) )
    }

    get membershipsWithCoiRequirement () {
        return this.memberships.filter(m => m.has_coi_requirement)
    }

    get needsCredentials () {
        return !this.credentials || this.credentials.length === 0
    }

    get hasExpertise () {
        return this.expertises && this.expertises.length > 0;
    }

    get needsExpertise () {
        return !this.hasExpertise;
    }

    get cocStatus () {
        return this.coc?.status ?? 'missing'
    }

    get cocIsDue () {
        return ['missing','expired','version_mismatch'].includes(this.cocStatus)
    }

    get coreMemberAttestationCompleted() {
        return !!this.core_member_attestation_completed;
    }

    matchesKeyword(keyword) {

        const {first_name, last_name, email} = this.attributes;
        const pattern = new RegExp(keyword, 'i');

        if (first_name && first_name.toLowerCase().match(pattern)) {
            return true;
        }
        if (last_name && last_name.toLowerCase().match(pattern)) {
            return true;
        }
        if (email && email.toLowerCase().match(pattern)) {
            return true;
        }
        if (first_name && last_name) {
            const fullname = `${first_name} ${last_name}`;
            if (fullname && fullname.toLowerCase().match(pattern)) {
                return true;
            }
        }

        return false;
    }

}

export default Person
